import React from 'react';
import { Smartphone, HeartPulse, ShoppingBag, Wallet, ArrowRight, Star } from 'lucide-react';
import { Card, Badge, Button } from '../ui';

export interface DoctorTerraCaseStudyProps {
  onOpenConsultation?: () => void;
}

export const DoctorTerraCaseStudy: React.FC<DoctorTerraCaseStudyProps> = ({ onOpenConsultation }) => {
  return (
    <section id="portfolio" className="py-12 sm:py-14 lg:py-16 relative bg-slate-100/70 dark:bg-[#080e1a] transition-colors overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">

        {/* Flagship Banner Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 sm:mb-10 gap-4">
          <div className="space-y-3 max-w-2xl">
            <Badge variant="accent" className="px-3.5 py-1 text-[11px] sm:text-xs uppercase tracking-wider">
              <Smartphone size={14} /> FEATURED MOBILE CASE STUDY
            </Badge>
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-[#184166] dark:text-white tracking-tight">
              Doctor Terra <span className="text-[#08adae]">Wellness & Member App</span>
            </h2>
            <p className="text-slate-600 dark:text-slate-300 text-sm sm:text-base leading-relaxed">
              Engineered by Technook Global, the Doctor Terra Wellness App is an all-in-one digital platform designed to help members seamlessly manage their health, digital shopping, and referral earnings in one convenient mobile application.
            </p>
          </div>

          <div className="shrink-0">
            <Badge variant="brand" className="px-4 py-2 text-xs font-mono font-bold">
              Doctor Terra Inc. Ecosystem
            </Badge>
          </div>
        </div>

        {/* Doctor Terra Mobile Case Study Showcase Layout */}
        <Card variant="glass" className="p-6 sm:p-10 bg-white dark:bg-[#0f1b2d] border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden relative">

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">

            {/* Left Mobile Device Mockup Frame */}
            <div className="lg:col-span-5 flex justify-center">
              <div className="relative max-w-xs sm:max-w-sm w-full p-4 rounded-[40px] bg-slate-900 border-4 border-slate-700 shadow-2xl shadow-cyan-950/20 group">
                <img
                  src="/assets/screens/Doctor-Terra-App.png"
                  alt="Doctor Terra Wellness App Screen"
                  className="w-full h-auto rounded-[32px] object-cover shadow-lg transition-transform duration-300 group-hover:scale-[1.02]"
                />

                {/* Micro Floating Badge */}
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-slate-950/90 backdrop-blur-md border border-slate-800 px-4 py-2 rounded-full text-center whitespace-nowrap shadow-xl">
                  <span className="text-xs font-bold text-white block">Doctor Terra Incorporated</span>
                  <span className="text-[10px] text-[#08adae] font-mono">All-in-One Member Ecosystem</span>
                </div>
              </div>
            </div>

            {/* Right Feature Breakdown Grid */}
            <div className="lg:col-span-7 space-y-6 sm:space-y-8">

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 rounded-2xl bg-[#f5f9fc] dark:bg-[#09101d] border border-slate-200 dark:border-slate-800 space-y-1.5">
                  <HeartPulse className="text-[#08adae]" size={22} />
                  <h4 className="text-xs font-bold text-[#184166] dark:text-white">Health Management</h4>
                  <p className="text-[11px] text-slate-600 dark:text-slate-400">Encrypted personal health vault and wellness tracking.</p>
                </div>

                <div className="p-4 rounded-2xl bg-[#f5f9fc] dark:bg-[#09101d] border border-slate-200 dark:border-slate-800 space-y-1.5">
                  <ShoppingBag className="text-[#4a82c2]" size={22} />
                  <h4 className="text-xs font-bold text-[#184166] dark:text-white">Digital Marketplace</h4>
                  <p className="text-[11px] text-slate-600 dark:text-slate-400">Curated e-commerce store for health & wellness products.</p>
                </div>

                <div className="p-4 rounded-2xl bg-[#f5f9fc] dark:bg-[#09101d] border border-slate-200 dark:border-slate-800 space-y-1.5">
                  <Wallet className="text-amber-500" size={22} />
                  <h4 className="text-xs font-bold text-[#184166] dark:text-white">Referral Earnings</h4>
                  <p className="text-[11px] text-slate-600 dark:text-slate-400">Multi-tier commission tracking & instant payout engine.</p>
                </div>
              </div>

              {/* Key Platform Stats */}
              <div className="grid grid-cols-3 gap-4 p-5 rounded-2xl bg-[#f5f9fc] dark:bg-[#09101d] border border-slate-200 dark:border-slate-800 text-center">
                <div>
                  <span className="text-xl sm:text-2xl font-black text-[#184166] dark:text-white font-mono block">50,000+</span>
                  <span className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Active Members</span>
                </div>
                <div>
                  <span className="text-xl sm:text-2xl font-black text-[#08adae] font-mono block">99.9%</span>
                  <span className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Platform SLA</span>
                </div>
                <div>
                  <span className="text-xl sm:text-2xl font-black text-[#4a82c2] font-mono block flex items-center justify-center gap-1">
                    4.9 <Star size={16} className="fill-amber-400 text-amber-400" />
                  </span>
                  <span className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">User Rating</span>
                </div>
              </div>

              {/* Call to Action */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
                <Button variant="primary" size="md" onClick={onOpenConsultation}>
                  <span>Request Custom Mobile Engineering</span>
                  <ArrowRight size={16} />
                </Button>
              </div>

            </div>

          </div>

        </Card>

      </div>
    </section>
  );
};
